/**
 * `?promo=CODE` on any link into the booking app (welcome back campaign,
 * 2026-10-26: `?promo=WELCOMEBACK`) fills the code in for the visitor: it
 * is read once at start-up (index.tsx, before the booking flow strips the
 * URL) and kept for the tab (sessionStorage), then applied by the booking
 * steps as soon as the basket can take it (consumer/usePendingPromo).
 *
 * Nothing here decides whether the code is good: the server checks it like
 * a typed code, and checks it again at booking.
 */
const KEY = 'apr.promo';
const APPLIED_KEY = 'apr.promo.applied';
const CODE = /^[A-Z0-9]{4,32}$/;
/** The gift card the link's offer carries (dollars), e.g. "10". */
const GIFT_KEY = 'apr.promo.gift';
/**
 * Links whose offer also carries a $10 Starbucks card after the first
 * visit: the "Go Oil is now Aperio" reactivation email and its reminder
 * (owner, 2026-11-01). The welcome back emails promised no card.
 */
const GIFT_SOURCES: Record<string, number> = { reactivation_email: 10 };

let memory: string | null = null;
let appliedMemory: string | null = null;
let giftMemory: number | null = null;

/** "welcomeback " -> "WELCOMEBACK"; null for anything that is not a code. */
export function normalizePromo(value: unknown): string | null {
  const code = String(value ?? '').trim().toUpperCase();
  return CODE.test(code) ? code : null;
}

function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value) window.sessionStorage.setItem(key, value);
    else window.sessionStorage.removeItem(key);
  } catch {
    // Private mode: the code still lives in memory for this page.
  }
}

/** Reads `?promo=` from the URL (once, at start-up). */
export function capturePromoParam(
  search: string = window.location.search,
): string | null {
  let code: string | null = null;
  try {
    code = normalizePromo(new URLSearchParams(search).get('promo'));
  } catch {
    code = null;
  }
  if (code) {
    memory = code;
    write(KEY, code);
    let src = '';
    try {
      src = String(new URLSearchParams(search).get('src') || '');
    } catch {
      src = '';
    }
    giftMemory = GIFT_SOURCES[src] || null;
    write(GIFT_KEY, giftMemory ? String(giftMemory) : null);
  }
  return code;
}

/** The code a link brought, until it is applied or removed. */
export function pendingPromo(): string | null {
  return memory || normalizePromo(read(KEY));
}

/** The visitor removed it (or it may not be used): stop applying it. */
export function clearPendingPromo(): void {
  memory = null;
  write(KEY, null);
}

/** The text of the code now applied to the booking ("WELCOMEBACK"). */
export function appliedPromoCode(): string | null {
  return appliedMemory || normalizePromo(read(APPLIED_KEY));
}

export function setAppliedPromoCode(code: string | null): void {
  appliedMemory = normalizePromo(code);
  write(APPLIED_KEY, appliedMemory);
}

/** The Starbucks card (dollars) the offer link promised, else null. */
export function promoGiftDollars(): number | null {
  if (giftMemory) return giftMemory;
  const n = Number(read(GIFT_KEY));
  return Number.isFinite(n) && n > 0 ? n : null;
}
